import { MoralisMarketDataTokenInterface, TokenInterface } from '@/interfaces';
import {
  fetchGlobalMarketData,
  fetchTopErc20TokensData,
} from '@/services/MoralisService';
import reactQueryClient from '@/services/ReactQueryClientService';
import { logError } from '@/utils/logger';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import React, { createContext, ReactNode, useContext, useMemo } from 'react';
interface MarketDataContextType {
  globalMarketData: MoralisMarketDataTokenInterface[];
  topErc20Tokens: TokenInterface[];
  isLoading: boolean;
  isTopErc20Loading: boolean;
  error: Error | null;
  refreshGlobalMarketData: () => void;
}
const MarketDataContext = createContext<MarketDataContextType | undefined>(
  undefined,
);
interface MarketDataProviderProps {
  children: ReactNode;
}
export const MarketDataProvider: React.FC<MarketDataProviderProps> = ({
  children,
}) => {
  const {
    data: globalMarketData = [],
    error,
    isLoading,
    refetch: refreshGlobalMarketData,
  } = useQuery({
    queryKey: ['globalMarketData'],
    queryFn: fetchGlobalMarketData,
    staleTime: 24 * 60 * 60 * 1000,
    retry: 1,
  });
  const {
    data: topErc20Tokens = [],
    error: topErc20Error,
    isLoading: isTopErc20Loading,
    refetch: refreshTopErc20Tokens,
  } = useQuery({
    queryKey: ['topErc20Tokens'],
    queryFn: fetchTopErc20TokensData,
    staleTime: 24 * 60 * 60 * 1000,
    retry: 1,
  });
  const value = useMemo(
    () => ({
      globalMarketData,
      topErc20Tokens,
      isLoading,
      isTopErc20Loading,
      error: error || topErc20Error || null,
      refreshGlobalMarketData,
      refreshTopErc20Tokens,
    }),
    [
      globalMarketData,
      topErc20Tokens,
      isLoading,
      isTopErc20Loading,
      error,
      topErc20Error,
      refreshGlobalMarketData,
      refreshTopErc20Tokens,
    ],
  );
  if (error) {
    logError(error, 'MarketDataProvider');
  }
  return (
    <QueryClientProvider client={reactQueryClient}>
      <MarketDataContext.Provider value={value}>
        {children}
      </MarketDataContext.Provider>
    </QueryClientProvider>
  );
};
export const useMarketData = (): MarketDataContextType => {
  const context = useContext(MarketDataContext);
  if (context === undefined) {
    throw new Error('useMarketData must be used within a MarketDataProvider');
  }
  return context;
};
